import React, { useMemo, useState, useEffect } from 'react';
import { Box, Divider, Skeleton } from '@mui/material';
import classNames from 'classnames';

import ErrorBoundary from 'components/ErrorBoundary';
import GraphTimeline from 'components/GraphTimeline';
import useCustomError from 'hooks/useCustomError';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { ConsistencyData } from 'types/consistency';

import ErrorMessage from '../ErrorMessage';
import DayNightShading from '../DayNightShading';
import ForecastGraphConsistencyContainer from './components/ForecastGraphConsistencyContainer';

import styles from './ForecastGraphConsistency.module.scss';

interface Props {
  daysToDisplay: Array<number>;
  consistency: ConsistencyData;
  sunlightTimes: SunlightTimes;
}

export const ForecastGraphConsistencyContent: React.FC<Props> = ({
  daysToDisplay,
  consistency,
  sunlightTimes,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const data = useMemo(
    () =>
      daysToDisplay
        ?.map((day) => consistency.days?.[day])
        ?.filter((day) => day)
        ?.flat(),
    [consistency, daysToDisplay],
  );

  const timeElements = useMemo(
    () =>
      daysToDisplay.map((day: number, dayIndex) => (
        <Box key={day} width="100%" display="flex">
          <Divider
            className={classNames(dayIndex !== 0 && styles.divider)}
            orientation="vertical"
            flexItem
            sx={{ border: 'none' }}
          />
          <div className={classNames(styles.dayWrapper)} key={daysToDisplay[dayIndex]}>
            <div
              className={classNames([styles.dayNightShading, 'day-night-shading'])}
              data-testid="day-night-shading-day"
            >
              <DayNightShading
                key={daysToDisplay[dayIndex]}
                sunlightTimes={sunlightTimes}
                numDays={daysToDisplay.length}
                forecastDayIndex={day}
                columnIndex={dayIndex}
                isPaywalled={false}
              />
            </div>
            {consistency.days?.[dayIndex] ? (
              <GraphTimeline day={consistency.days[daysToDisplay[dayIndex]] as any} showMarkers />
            ) : null}
          </div>
        </Box>
      )),
    [daysToDisplay, consistency.days, sunlightTimes],
  );

  // Show loading skeleton during SSR and while data is loading to prevent hydration mismatch
  return consistency?.loading || !isMounted ? (
    <Skeleton
      className={styles.loading}
      data-testid="wave-consistency-graph-loading"
      variant="rectangular"
    />
  ) : (
    <div className={styles.wrapper} data-testid="wave-consistency-graph">
      <div className={styles.content}>
        <ForecastGraphConsistencyContainer data={data} />
        {timeElements}
      </div>
    </div>
  );
};

const ForecastGraphConsistency: React.FC<Props> = (props) => {
  const {
    consistency: { error },
  } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-consistency"
      render={() => (
        <Box className={styles.error} data-testid="wave-consistency-graph-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <ForecastGraphConsistencyContent {...props} />
    </ErrorBoundary>
  );
};

export default ForecastGraphConsistency;
